import { base64 } from '@scure/base';
import { Transaction } from '@scure/btc-signer';
import { TransactionInput } from '@scure/btc-signer/psbt';

import { BtcLikeNetwork } from '../types';

import { type ResultUtxo } from './api';
import { checkCpfpFee, checkUtxoCombinationTxFee, findFittingBtcUtxoInputCombination } from './calculateFee';
import { addInputUTXOsToTx, calculateTxInputsValue } from './utils';

type CreateSendTransactionParams = {
  publicKey: string;
  recipient: string;
  walletAddress: string;
  amount: number;
  vbyteEstimate?: number;
  enforcedUtxoSelection?: ResultUtxo[];
  network: BtcLikeNetwork;
};

export const createSendTransaction = async ({
  publicKey,
  recipient,
  walletAddress,
  amount,
  vbyteEstimate,
  enforcedUtxoSelection,
  network,
}: CreateSendTransactionParams) => {
  // Create a new transaction
  let tx = new Transaction();

  if (walletAddress !== recipient) {
    // Add the recipient output
    tx.addOutputAddress(recipient, BigInt(amount), network.Settings);
  }

  const { txFees, satPerVbyteEstimate } = await (async () => {
    if (Array.isArray(enforcedUtxoSelection)) {
      // Calculate fees based on manually selected UTXOs
      tx = (
        await addInputUTXOsToTx({
          tx,
          utxos: enforcedUtxoSelection,
          walletAddress,
          publicKey,
          network,
        })
      ).txWithInputs;

      const satPerVbyteEstimate = vbyteEstimate || (await network.api.getSatPerVbEstimateFee()).economyFee;

      const { totalCpfpFee } = await checkCpfpFee({ tx, satPerVbyteEstimate, apiEndpoint: network.MempoolApiEndpoint });

      const txFees = await checkUtxoCombinationTxFee({
        estimationTx: tx,
        utxosCombination: [],
        satPerVbyteEstimate,
        walletAddress,
        publicKey,
        mempoolTip: totalCpfpFee,
        network,
      });

      return { txFees, satPerVbyteEstimate };
    } else {
      // Select first fitting UTXOs combination to fund the transaction
      const {
        combination: fittingUtxosCombination,
        satPerVbyteEstimate,
        ...txFees
      } = await findFittingBtcUtxoInputCombination({
        tx,
        walletAddress,
        publicKey,
        vbyteEstimate,
        network,
      });

      tx = (
        await addInputUTXOsToTx({
          tx,
          utxos: fittingUtxosCombination,
          walletAddress,
          publicKey,
          network,
        })
      ).txWithInputs;

      return { txFees, satPerVbyteEstimate };
    }
  })();

  // Add change output if there's any change
  if (txFees.change > 0n) {
    tx.addOutputAddress(walletAddress, BigInt(txFees.change), network.Settings);
  }

  const psbt = tx.toPSBT(0);
  const psbtB64 = base64.encode(psbt);

  // Calculate resulting input value - do not include the dust lock input
  const inputValue = await calculateTxInputsValue(tx['inputs'] as TransactionInput[], network.MempoolApiEndpoint);

  return {
    tx,
    psbt,
    inputAmount: inputValue,
    psbtB64,
    isChange: txFees.change > 0n,
    satPerVbyteEstimate,
    ...txFees,
    sighTx: Array.from({ length: tx.inputsLength }, (_, i) => i),
  };
};
